import xlsxExport from '../shared/xlsxExport/xlsxExport';
import {
  trialBalanceColumnDefsTpc,
  trialBalanceColumnDefsEsdrm,
} from '../shared/columnDefinitions/trialBalance';
import { journalGlobalColumnDefs, journalLocalColumnDefs, journalsDetailColumnDefs } from '../shared/columnDefinitions/journals';
import { adjustmentsColumnCollectionDefs } from '../shared/columnDefinitions/adjustments';
import { calcSettingsBookToTaxDifferencesColumnDefs, adjustmentsTabBookToTaxDifferencesColumnDefs } from '../shared/columnDefinitions/bookToTaxDifferences';
import { SheetExportTypes, CUSTOM_PERCENT_6_CELL_FORMAT } from '@common/xlsxExport/xlsxExport.constants';
import { calcSettingsTaxRatesColumnDefs } from '../shared/columnDefinitions/taxRates';
import calcSettingsTaxAccountingColumnDefs from '../shared/columnDefinitions/taxAccountingForProvisionPage';
import { YES_FLAG } from '../shared/constants';

export default ({
  period,
  general,
  taxExpense,
  trialBalanceCollection,
  journalsCollection,
  localJournalsCollection,
  journalsDetailCollection,
  adjustmentsCollection,
  bookToTaxDifferencesCollection,
  taxRates,
  taxAccounting,
  bookToTaxDifferences,
  shouldDisplayZero,
  hideDecimals,
  negativeMinus,
  environmentSuffix,

  detailRows,
}) => {
  const {
    companyCode,
    legalEntity,
    businessUnit,
    associatedLeGoldId,
    associatedLeCountryCode,
    associatedLeUsTaxType,
    associatedLeLocalTaxType,
    connName,
    chartName,
    functionalCurrency,
    statCurrency,
    overrideTaxCurrency,
    usShareholderFlag,
    associatedLeBranch,
    homeOfficeCompanyCode,
    homeOfficeCountryCode,
    recordHomeOfficeTaxes,
    recordFtc,
    ledgerId,
    subledgerFlag,
    statCalc,
  } = general.companyCodeDetails;

  const columnDefs = chartName === 'R2R' ? trialBalanceColumnDefsEsdrm : trialBalanceColumnDefsTpc;

  xlsxExport({
    sheetData: [
      ...([
        {
          name: 'US Summary Tax Expense',
          data: taxExpense.US_SUMMARY,
          type: SheetExportTypes.REPORT,
          descriptionColumnIndex: 2,
          options: {
            omitExportIfNull: true,
            shouldDisplayZero,
            hideDecimals,
            negativeMinus,
            addTimestamp: true,
            splitDescriptionColumn: true,
            groupByTag: true,
            detailRows: detailRows.US_SUMMARY,
          },
        },
        {
          name: 'US Federal Tax Expense',
          data: taxExpense.US_TED,
          type: SheetExportTypes.REPORT,
          descriptionColumnIndex: 2,
          options: {
            omitExportIfNull: true,
            shouldDisplayZero,
            hideDecimals,
            negativeMinus,
            addTimestamp: true,
            splitDescriptionColumn: true,
            groupByTag: true,
            dataColumnStartIndex: 0,
            dataColumnEndIndex: 1,
            detailRows: detailRows.US_FED,
          },
        },
        {
          name: 'US State Tax Expense',
          data: taxExpense.US_STATE,
          type: SheetExportTypes.REPORT,
          descriptionColumnIndex: 2,
          options: {
            omitExportIfNull: true,
            numberOfColumnsToStick: 3,
            shouldDisplayZero,
            hideDecimals,
            negativeMinus,
            splitDescriptionColumn: true,
            groupByTag: true,
            detailRows: detailRows.US_STATE,
          },
        },
        {
          name: 'GAAP Non-US Tax Expense',
          data: taxExpense.TED_NON_US_GAAP,
          type: SheetExportTypes.REPORT,
          descriptionColumnIndex: 2,
          options: {
            omitExportIfNull: true,
            shouldDisplayZero,
            hideDecimals,
            negativeMinus,
            splitDescriptionColumn: true,
            groupByTag: true,
            detailRows: detailRows.TED_NON_US_GAAP,
          },
        },
        {
          name: 'STAT Non-US Tax Expense',
          data: taxExpense.TED_NON_US_STAT,
          type: SheetExportTypes.REPORT,
          descriptionColumnIndex: 2,
          options: {
            omitExportIfNull: true,
            shouldDisplayZero,
            hideDecimals,
            negativeMinus,
            splitDescriptionColumn: true,
            groupByTag: true,
            detailRows: detailRows.TED_NON_US_STAT,
          },
        },
      ].filter(report => report.data && report.data.showReport)),
      ...([
        {
          name: 'Journals',
          data: journalsCollection,
          columnDefinitions: journalGlobalColumnDefs,
          type: SheetExportTypes.GRID,
          options: {
            shouldDisplayZero,
            hideDecimals,
            negativeMinus,
          },
        },
        chartName !== 'R2R' ? {
          name: 'Local Journals',
          data: localJournalsCollection,
          columnDefinitions: journalLocalColumnDefs,
          type: SheetExportTypes.GRID,
          options: {
            shouldDisplayZero,
            hideDecimals,
            negativeMinus,
          },
        } : null,
        {
          name: 'Journals Detail',
          data: journalsDetailCollection,
          columnDefinitions: journalsDetailColumnDefs,
          type: SheetExportTypes.GRID,
          options: {
            shouldDisplayZero,
            hideDecimals,
            negativeMinus,
          },
        },
        {
          name: 'GAAP Trial Balance',
          data: trialBalanceCollection.filter(({ BOOK_TYPE }) => BOOK_TYPE.toLowerCase() === 'p'),
          columnDefinitions: columnDefs,
          type: SheetExportTypes.GRID,
          options: {
            shouldDisplayZero,
            hideDecimals,
            negativeMinus,
          },
        },
        statCalc === YES_FLAG ? {
          name: 'G2S Trial Balance',
          data: trialBalanceCollection.filter(({ BOOK_TYPE }) => BOOK_TYPE.toLowerCase() === 'g'),
          columnDefinitions: columnDefs,
          type: SheetExportTypes.GRID,
          options: {
            shouldDisplayZero,
            hideDecimals,
            negativeMinus,
          },
        } : null,
        statCalc === YES_FLAG ? {
          name: 'STAT Trial Balance',
          data: trialBalanceCollection,
          columnDefinitions: columnDefs,
          type: SheetExportTypes.GRID,
          options: {
            shouldDisplayZero,
            hideDecimals,
            negativeMinus,
          },
        } : null,
      ].filter(grid => grid)),
      {
        name: 'Calc Settings - Company Code',
        data: [
          ['Company Code', companyCode, 'Override Tax Currency', overrideTaxCurrency],
          ['Legal Entity', legalEntity, 'US Shareholder Flag', usShareholderFlag],
          ['Business Unit', businessUnit, 'Branch', associatedLeBranch],
          ['GOLD ID', associatedLeGoldId, 'Home Office Company Code', homeOfficeCompanyCode],
          ['Home Office Country Code', homeOfficeCountryCode],
          ['Country of Domicile', associatedLeCountryCode, 'Record Home Office Taxes', recordHomeOfficeTaxes],
          ['US Tax Type', associatedLeUsTaxType, 'Record FTC', recordFtc],
          ['Local Tax Type', associatedLeLocalTaxType, 'Ledger ID', ledgerId ? ledgerId.toString() : ''],
          ['Connection', connName, 'Subledger Ready', subledgerFlag],
          ['Chart', chartName, 'STAT Calc Flag', statCalc],
          ['Functional Currency', functionalCurrency, 'STAT Currency', statCurrency],
        ],
        customCellFormatters: {
          numberFormatter: value => ({
            customFormat: CUSTOM_PERCENT_6_CELL_FORMAT,
            value: value ? value : 0,
          }),
        },
        type: SheetExportTypes.CUSTOM,
        options: {
          shouldDisplayZero,
          hideDecimals,
          negativeMinus,
        },
      },
      {
        name: 'Calc Settings - Tax Rates',
        data: taxRates,
        columnDefinitions: calcSettingsTaxRatesColumnDefs,
        type: SheetExportTypes.GRID,
        options: {
          shouldDisplayZero,
          hideDecimals,
          negativeMinus,
        },
      },
      {
        name: 'Calc Settings - Book to Tax Differences',
        data: bookToTaxDifferences,
        columnDefinitions: calcSettingsBookToTaxDifferencesColumnDefs,
        type: SheetExportTypes.GRID,
        options: {
          shouldDisplayZero,
          hideDecimals,
          negativeMinus,
        },
      },
      {
        name: 'Calc Settings - Tax Accounting',
        data: taxAccounting,
        columnDefinitions: calcSettingsTaxAccountingColumnDefs,
        type: SheetExportTypes.GRID,
        options: {
          shouldDisplayZero,
          hideDecimals,
          negativeMinus,
        },
      },
      {
        name: 'Adjustments',
        data: adjustmentsCollection,
        columnDefinitions: adjustmentsColumnCollectionDefs,
        type: SheetExportTypes.GRID,
        options: {
          shouldDisplayZero,
          hideDecimals,
          negativeMinus,
        },
      },
      {
        name: 'Book To Tax Differences',
        data: bookToTaxDifferencesCollection,
        columnDefinitions: adjustmentsTabBookToTaxDifferencesColumnDefs,
        type: SheetExportTypes.GRID,
        options: {
          shouldDisplayZero,
          hideDecimals,
          negativeMinus,
        },
      },
    ],
    metadata: {
      description: `${companyCode} - ${general.name}`,
      periodName: period.name,
      fileName: `TLS_Provision_Results_${period.name}_${companyCode}${environmentSuffix}`,
    },
  });
};
